// The lanes and lane-restriction view of an RM or OSMLR way (Orbis spec 12.18):
// per stretch, the slots with direction and dividers, then one row per known slot
// key, and the restriction class per slot as the map colours it.
import {
  accessClassOf,
  rmLaneAccess,
} from "../layers/style/util/restrictionClass.mjs";
import { baseKey } from "./lrValues.mjs";
import { isLaneCountKey, splitSlots } from "./slots.mjs";
import { buildStretches, tagSections } from "./stretches.mjs";

const LANE_DRAWN_KEYS = ["lanes:direction", "divider:lanes"];

const LANE_ROW_KEYS =
  /^(turn:lanes|exit_entrance:lanes|lanes:direction:conditional|turn:lanes:conditional|lanes:vehicle_access:(bus|hgv|motorcar|taxi)(:conditional)?|hov:lanes(:conditional)?|hov:minimum:lanes(:conditional)?|hov:clean_fuel:lanes(:conditional)?|toll:lanes(:conditional)?|toll:hov:lanes(:conditional)?)$/;

const ACCESS_MODES = ["bus", "hgv", "motorcar", "taxi"];
const ACCESS_KEYS = ACCESS_MODES.map((m) => `lanes:vehicle_access:${m}`);
const ACCESS_FIELDS = {
  bus: "rm_vehicleAccessBus",
  hgv: "rm_vehicleAccessHgv",
  motorcar: "rm_vehicleAccessMotorcar",
  taxi: "rm_vehicleAccessTaxi",
};

// Row order; each plain key before its :conditional form.
const ROW_ORDER = [
  "turn:lanes",
  "exit_entrance:lanes",
  "lanes:direction",
  ...ACCESS_KEYS,
  "hov:lanes",
  "hov:minimum:lanes",
  "hov:clean_fuel:lanes",
  "toll:lanes",
  "toll:hov:lanes",
].flatMap((key) =>
  key === "lanes:direction"
    ? [`${key}:conditional`]
    : [key, `${key}:conditional`],
);

const pad = (slots, length) => Array.from({ length }, (_, i) => slots[i] ?? "");

/**
 * @param {import("./featureModel.mjs").FeatureModel} model
 * @returns {{stretches: object[], usedTags: object[]}|null}
 */
export function buildLaneView(model) {
  const tagsByKey = new Map();
  model.groups.forEach((group) =>
    group.tags.forEach((tag) => {
      const key = baseKey(tag.key);
      const known =
        LANE_DRAWN_KEYS.includes(key) ||
        LANE_ROW_KEYS.test(key) ||
        isLaneCountKey(key);
      if (known && !tagsByKey.has(key) && tagSections(tag))
        tagsByKey.set(key, tag);
    }),
  );
  const shown = [...tagsByKey.keys()].filter((key) => !isLaneCountKey(key));
  if (shown.length === 0) return null;

  const hasAccess = [...tagsByKey.keys()].some((key) =>
    key.startsWith("lanes:vehicle_access:"),
  );
  const rowKeys = ROW_ORDER.filter(
    (key) => tagsByKey.has(key) || (hasAccess && ACCESS_KEYS.includes(key)),
  );

  const sectionsByKey = Object.fromEntries(
    [...tagsByKey].map(([key, tag]) => [key, tagSections(tag)]),
  );
  const stretches = buildStretches(sectionsByKey, model.lengthCm).map(
    ({ startCm, endCm, values }) => {
      const slotsOf = (key) =>
        values[key] === undefined ? null : splitSlots(values[key]);
      const direction = slotsOf("lanes:direction");
      const dividers = slotsOf("divider:lanes");
      const rowSlots = rowKeys.map((key) => [key, slotsOf(key)]);
      const slotCount = Math.max(
        Number(values["lanes"]) || 0,
        direction ? direction.length : 0,
        dividers ? dividers.length + 1 : 0,
        ...rowSlots.map(([, slots]) => (slots ? slots.length : 0)),
      );
      const rows = rowSlots
        .filter(([key, slots]) => slots || ACCESS_KEYS.includes(key))
        .map(([key, slots]) => ({
          key,
          tagged: tagsByKey.has(key),
          slots: pad(slots ?? [], slotCount),
        }));
      const counts = {};
      ["forward", "backward", "both_ways", "reversible"].forEach((name) => {
        const value = values[`lanes:${name}`];
        if (value !== undefined) counts[name] = value;
      });
      const accessSlots = ACCESS_MODES.map((mode) =>
        pad(slotsOf(`lanes:vehicle_access:${mode}`) ?? [], slotCount),
      );
      const classes = hasAccess
        ? Array.from({ length: slotCount }, (_, i) =>
            accessClassOf(
              rmLaneAccess(
                Object.fromEntries(
                  ACCESS_MODES.map((mode, m) => [
                    ACCESS_FIELDS[mode],
                    accessSlots[m][i],
                  ]),
                ),
              ),
            ),
          )
        : null;
      return {
        startCm,
        endCm,
        slotCount,
        lanes: values["lanes"],
        counts,
        direction: direction && pad(direction, slotCount),
        dividers: dividers && pad(dividers, Math.max(slotCount - 1, 0)),
        rows,
        classes,
      };
    },
  );
  return { stretches, usedTags: [...tagsByKey.values()] };
}
